import React from "react";
import { Checkbox, Row, Col } from "antd";

const PermissionsSection = ({
  state,
  upperPermissions,
  setState,
  setUpperPermissions,
  readOnly,
}) => {
  const onChange = (checked, label, key, prevData, setHandler) => {
    // Ensure prevData is an object
    prevData = prevData || {};
    
    // If trying to check delete or export but view is not checked, prevent it
    if (label !== "navisworks_file_export" && !prevData?.view && checked && ["export", "delete"].includes(key)) {
      return;
    }

    // If unchecking view but add_edit is checked, prevent it
    if (key === "view" && !checked && prevData?.add_edit) {
      return;
    }

    // If checking add_edit, also check view but preserve delete state only if applicable
    if (key === "add_edit" && checked) {
      const next = {
        ...prevData,
        [key]: checked,
        view: true,
      };
      // Only carry delete if this permission group supports it
      if (Object.prototype.hasOwnProperty.call(prevData || {}, 'delete')) {
        next.delete = prevData.delete !== undefined ? prevData.delete : false;
      }
      let obj = {
        [label]: next,
      };
      return setHandler((prev) => ({
        ...prev,
        ...obj,
      }));
    }

    // If unchecking view, also uncheck export and delete (when applicable)
    if (key === "view" && !checked) {
      const updates = {
        [key]: checked,
      };
      
      // Only add export: false if the permission type has export (project_setup)
      if (prevData.hasOwnProperty('export')) {
        updates.export = false;
      }
      // Only add delete: false if the permission type has delete
      if (prevData.hasOwnProperty('delete')) {
        updates.delete = false;
      }
      
      let obj = {
        [label]: {
          ...prevData,
          ...updates
        },
      };
      
      // If unchecking project_setup view, also uncheck navisworks_file_export
      if (label === 'project_setup') {
        obj.navisworks_file_export = {
          export: false
        };
      }
      
      return setHandler((prev) => ({
        ...prev,
        ...obj,
      }));
    }

    // Default case for all other permission changes
    let obj = {
      [label]: {
        ...prevData,
        [key]: checked,
      },
    };
    return setHandler((prev) => ({
      ...prev,
      ...obj,
    }));
  };

  const handleLabel = (labelText) => {
    return labelText.split("_").join(" ");
  };

  const checkText = (labelText) => {
    return labelText.split("_").join(" / ");
  };

  // Create a single box for project_setup, project_weightings, project_priorities, and navisworks_file_export
  const projectPermissions = ['project_setup', 'project_weightings', 'project_priorities', 'navisworks_file_export'];
  
  // Create a single row for all project permissions
  const upperRow = (
    <Col className="general-permissions-section upper-row">
      <Row style={{ width: '100%' }}>
        {/* PROJECT SETUP column */}
        <Col span={6}>
          <div className="label-div">
            <span className="uppercase-label label-div" style={{fontWeight: 'ExtraBold !important'}}>PROJECT SETUP</span>
          </div>
          {Object.entries(upperPermissions?.project_setup || {
            add_edit: false,
            view: false,
            export: false,
            delete: false
          })
          .filter(([key]) => key !== 'export') // Filter out export checkbox
          .map(([key, value]) => (
            <div className="checkbox-div" key={key}>
              <Checkbox
                checked={value}
                disabled={
                  !readOnly ||
                  (!upperPermissions?.project_setup?.view && ["export", "delete"].includes(key))
                }
                onChange={({ target }) =>
                  onChange(
                    target.checked,
                    'project_setup',
                    key,
                    upperPermissions?.project_setup,
                    setUpperPermissions
                  )
                }
              >
                {checkText(key)}
              </Checkbox>
            </div>
          ))}
        </Col>
        
        {/* PROJECT WEIGHTINGS column */}
        <Col span={6}>
          <div className="label-div">
            <span className="uppercase-label label-div" style={{fontWeight: 'ExtraBold !important'}}>PROJECT WEIGHTINGS</span>
          </div>
          {Object.entries(upperPermissions?.project_weightings || {
            add_edit: false,
            view: false,
            delete: false
          }).map(([key, value]) => (
            <div className="checkbox-div" key={key}>
              <Checkbox
                checked={value}
                disabled={
                  !readOnly ||
                  (!upperPermissions?.project_weightings?.view && ["delete"].includes(key))
                }
                onChange={({ target }) =>
                  onChange(
                    target.checked,
                    'project_weightings',
                    key,
                    upperPermissions?.project_weightings,
                    setUpperPermissions
                  )
                }
              >
                {checkText(key)}
              </Checkbox>
            </div>
          ))}
        </Col>
        
        {/* PROJECT PRIORITIES column */}
        <Col span={6}>
          <div className="label-div">
            <span className="uppercase-label label-div" style={{fontWeight: 'ExtraBold !important'}}>PROJECT PRIORITIES</span>
          </div>
          {Object.entries(upperPermissions?.project_priorities || {
            add_edit: false,
            view: false,
            delete: false
          }).map(([key, value]) => (
            <div className="checkbox-div" key={key}>
              <Checkbox
                checked={value}
                disabled={
                  !readOnly ||
                  (!upperPermissions?.project_priorities?.view && ["delete"].includes(key))
                }
                onChange={({ target }) =>
                  onChange(
                    target.checked,
                    'project_priorities',
                    key,
                    upperPermissions?.project_priorities,
                    setUpperPermissions
                  )
                }
              >
                {checkText(key)}
              </Checkbox>
            </div>
          ))}
        </Col>
        
        {/* NAVISWORKS SHAREPOINT column */}
        <Col span={6}>
          <div className="label-div">
            <span className="uppercase-label label-div" style={{fontWeight: 'ExtraBold !important', whiteSpace: 'nowrap'}}>NAVISWORKS FILE EXPORT</span>
          </div>
          {Object.entries(upperPermissions?.navisworks_file_export || {
            export: false
          })
            .filter(([key]) => ["export"].includes(key))
            .map(([key, value]) => (
            <div className="checkbox-div" key={key}>
              <Checkbox
                checked={value}
                disabled={
                  !readOnly || 
                  !upperPermissions?.project_setup?.view
                }
                onChange={({ target }) =>
                  onChange(
                    target.checked,
                    'navisworks_file_export',
                    key,
                    upperPermissions?.navisworks_file_export,
                    setUpperPermissions
                  )
                }
              >
                {checkText(key)}
              </Checkbox>
            </div>
          ))}
        </Col>
      </Row>
    </Col>
  );
  
  // Filter out the project permissions from upperPermissions for other rows
  const otherUpperRows = Object.entries(upperPermissions || {}).map((data) => {
    const [label, checkBoxData] = data;
    
    // Skip project permissions as they're handled separately
    if (projectPermissions.includes(label)) {
      return null;
    }
    
    // Ensure checkBoxData is an object with the expected properties
    const safeCheckBoxData = checkBoxData || {
      add_edit: false,
      view: false,
      export: false,
      delete: false
    };
    
    return (
      <Col key={label} className="general-permissions-section upper-row">
        <div className="label-div">
          <span className="uppercase-label ">{handleLabel(label)}</span>
        </div>
        {Object.entries(safeCheckBoxData)
          .filter(([key]) => key !== 'export') // Filter out export checkbox
          .map((data2) => {
          const [key, value] = data2;
          return (
            <div className="checkbox-div" key={key}>
              <Checkbox
                key={key}
                checked={value}
                disabled={
                  !readOnly ||
                  (!safeCheckBoxData.view && ["export", "delete"].includes(key))
                }
                onChange={({ target }) =>
                  onChange(
                    target.checked,
                    label,
                    key,
                    safeCheckBoxData,
                    setUpperPermissions
                  )
                }
              >
                {checkText(key)}
              </Checkbox>
            </div>
          );
        })}
      </Col>
    );
  }).filter(Boolean); // Filter out null values

  const jsx = Object.entries(state || {}).map((data) => {
    const [label, checkBoxData] = data;
    return (
      <Col key={label} className="general-permissions-section multiple-col">
        <div className="label-div ">
          <span
            style={{
              textTransform: "capitalize",
              fontWeight: 'lighter !important',
              fontSize: "12px",
              lineHeight: "14px",
              whiteSpace: "nowrap"
            }}
          >
            {handleLabel(label)}
          </span>
        </div>
        {Object.entries(checkBoxData || {}).map((data2) => {
          const [key, value] = data2;

          return (
            <div className="checkbox-div" key={key}>
              <Checkbox
                key={key}
                disabled={!readOnly}
                checked={value}
                onChange={({ target }) =>
                  onChange(target.checked, label, key, checkBoxData, setState)
                }
              >
                {checkText(key)}
              </Checkbox>
            </div>
          );
        })}
      </Col>
    );
  });

  return (
    <>
      <Col className="parent-div">
        <Row gutter={[20, 30]}>
          <Col
            className="general-permission-upper-div"
            xxl={22}
            xl={22}
            lg={22}
            md={22}
          >
            <Row>{upperRow}</Row>
            {otherUpperRows.length > 0 && (
              <Row style={{ marginTop: '20px' }}>{otherUpperRows}</Row>
            )}
          </Col>
          <Col
            className="permissions  setting-permissions"
            xxl={20}
            xl={22}
            lg={20}
            md={24}
          >
            <div className="settings-div">Settings</div>
            <Row className="permission-row-div" style={{ display: 'flex', flexWrap: 'nowrap' }}>{jsx}</Row>
          </Col>
        </Row>
      </Col>
    </>
  );
};

export default PermissionsSection;
